import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface SalesVolumeFilterData {
  hideLocationWithoutSales: boolean;
  minValue: number;
  maxValue: number;
  minInput: string;
  maxInput: string;
  appliedFilters: string[];
}

@Component({
  standalone: false,
  selector: 'mtn-sales-volume-filter',
  templateUrl: './sales-volume-filter.component.html',
  styleUrls: ['./sales-volume-filter.component.scss']
})
export class SalesVolumeFilterComponent {
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() apply = new EventEmitter<SalesVolumeFilterData>();
  @Output() reset = new EventEmitter<void>();

  // Filter data
  hideLocationWithoutSales = false;
  minValue = 900000;
  maxValue = 33600000;
  minInput = '800,000';
  maxInput = '33.6';
  minError = 'Value must be between 900,000 and 134,500,000';
  showMinError = true;
  showMaxError = false;

  // Applied filters for sidebar
  appliedFilters = [
    'Sales Volume',
    'Geography',
    'Company',
    'Landlord',
    'SQFT'
  ];

  // Sidebar navigation items
  sidebarItems = [
    { 
      name: 'Sales Volume', 
      icon: 'layout-grid-line', 
      count: 2, 
      isActive: true,
      isHover: true
    },
    { 
      name: 'Geography', 
      icon: 'calendar-line', 
      count: 5, 
      isActive: false,
      isHover: false
    },
    { 
      name: 'Company', 
      icon: 'user-line', 
      count: 2, 
      isActive: false,
      isHover: false
    },
    { 
      name: 'Landlord', 
      icon: 'building-line', 
      count: 2, 
      isActive: false,
      isHover: false
    },
    { 
      name: 'SQFT', 
      icon: 'user-location-line', 
      count: 2, 
      isActive: false,
      isHover: false
    }
  ];

  onHideLocationChange(): void {
    this.hideLocationWithoutSales = !this.hideLocationWithoutSales;
  }

  onMinInputChange(value: string): void {
    this.minInput = value;
    // Validate min input
    const numValue = parseFloat(value.replace(/,/g, ''));
    this.showMinError = isNaN(numValue) || numValue < 900000 || numValue > 134500000;
  }

  onMaxInputChange(value: string): void {
    this.maxInput = value;
    // Basic validation for max input
    const numValue = parseFloat(value);
    this.showMaxError = isNaN(numValue) || numValue < 0;
  }

  onSliderMinChange(value: number): void {
    this.minValue = value;
  }

  onSliderMaxChange(value: number): void {
    this.maxValue = value;
  }

  onReset(): void {
    this.hideLocationWithoutSales = false;
    this.minValue = 900000;
    this.maxValue = 33600000;
    this.minInput = '900,000';
    this.maxInput = '33.6';
    this.showMinError = false;
    this.showMaxError = false;
    this.reset.emit();
  }

  onCancel(): void {
    this.close.emit();
  }

  onApply(): void {
    const filterData: SalesVolumeFilterData = {
      hideLocationWithoutSales: this.hideLocationWithoutSales,
      minValue: this.minValue,
      maxValue: this.maxValue,
      minInput: this.minInput,
      maxInput: this.maxInput,
      appliedFilters: this.appliedFilters
    };
    this.apply.emit(filterData);
  }

  onSidebarItemClick(item: any): void {
    // Handle sidebar navigation
    this.sidebarItems.forEach(i => {
      i.isActive = false;
      i.isHover = false;
    });
    item.isActive = true;
    item.isHover = true;
  }

  formatDisplayValue(): string {
    return `${(this.minValue / 1000000).toFixed(1)},${(this.maxValue / 1000000).toFixed(1)} $`;
  }
}
